// SPDX-License-Identifier: GPL-3.0-only
// Copyright (C) 2026 Kyle Johnson
// This file is part of Hushold.
//
// Hushold is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as
// published by the Free Software Foundation, version 3.

import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useApp } from '../../store/AppContext.js';
import { formatAmount, ASSET_TYPES, LIABILITY_TYPES } from '../../lib/eventLog.js';
import { getExchangeRate, convertCurrency } from '../../lib/currency-math.js';
import { getCurrencyDecimalPlaces } from '../../lib/currencies.js';
import { formatDate } from '../../lib/formatLocale.js';
import type { Account } from '../../types.js';

export function BalanceSheet() {
  const { t } = useTranslation(['reports', 'common']);
  const { state } = useApp();
  const { ledger } = state;
  const homeCurrency = ledger.settings.homeCurrency;

  const today = new Date().toISOString().slice(0, 10);
  const allPrices = useMemo(() => [...ledger.prices.values()], [ledger.prices]);

  function toHome(account: Account): { homeAmount: number; hasRate: boolean } {
    if (account.currency === homeCurrency) return { homeAmount: account.balance, hasRate: true };
    const rate = getExchangeRate(account.currency, homeCurrency, today, allPrices);
    if (rate === null) return { homeAmount: 0, hasRate: false };
    return {
      homeAmount: convertCurrency(
        account.balance, account.currency, homeCurrency, rate,
        getCurrencyDecimalPlaces(account.currency), getCurrencyDecimalPlaces(homeCurrency),
      ),
      hasRate: true,
    };
  }

  const accounts = [...ledger.accounts.values()];
  const assets = accounts.filter((a) => ASSET_TYPES.has(a.type) && !a.placeholder);
  const liabilities = accounts.filter((a) => LIABILITY_TYPES.has(a.type) && !a.placeholder);
  const equity = accounts.filter((a) => a.type === 'EQUITY' && !a.placeholder);

  const hasForeignAccounts = assets.concat(liabilities).concat(equity).some((a) => a.currency !== homeCurrency);

  const totalAssets = assets.reduce((sum, a) => sum + toHome(a).homeAmount, 0);
  const totalLiabilities = liabilities.reduce((sum, a) => sum + toHome(a).homeAmount, 0);
  const totalEquity = equity.reduce((sum, a) => sum + toHome(a).homeAmount, 0);

  function SectionTable({ label, rows }: { label: string; rows: typeof assets; total: number }) {
    const total = rows.reduce((sum, a) => sum + toHome(a).homeAmount, 0);
    return (
      <div className="mb-4">
        <div className="px-4 py-1.5 border-b border-border-strong bg-bg-elevated print:border-gray-300 print:bg-gray-50">
          <h3 className="text-sm font-semibold text-text-muted uppercase tracking-wide print:text-gray-700">{label}</h3>
        </div>
        <table className="w-full">
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={3} className="py-3 text-center text-sm text-text-subtle print:text-gray-400">{t('common:emptyState.none')}</td></tr>
            )}
            {rows.map((a) => {
              const { homeAmount, hasRate } = toHome(a);
              const isForeign = a.currency !== homeCurrency;
              return (
                <tr key={a.id} className="border-b border-border print:border-gray-100">
                  <td className="py-1.5 px-4 text-sm text-text-muted print:text-gray-700">
                    {a.name}
                    {isForeign && <span className="ml-1 text-xs text-text-subtle font-mono print:text-gray-400">{a.currency}</span>}
                  </td>
                  {isForeign ? (
                    <td className="py-1.5 px-2 text-right text-xs font-mono text-text-subtle print:text-gray-500">
                      {formatAmount(a.balance, a.currency)}
                    </td>
                  ) : <td />}
                  <td className="py-1.5 px-4 text-right text-sm font-mono text-text print:text-gray-900">
                    {hasRate ? formatAmount(homeAmount, homeCurrency) : <span className="text-text-subtle print:text-gray-400">{t('reports:noRate')}</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot className="border-t border-border-strong print:border-gray-300">
            <tr>
              <td className="py-1.5 px-4 text-sm font-semibold text-text-muted print:text-gray-700">{t('reports:sections.totalLabel', { label })}</td>
              <td />
              <td className="py-1.5 px-4 text-right text-sm font-mono font-semibold text-text print:text-gray-900">
                {formatAmount(total, homeCurrency)}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-auto p-4">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-md font-semibold text-text">{t('reports:titles.balanceSheet')}</h2>
        <div className="flex items-center gap-3">
          <span className="text-sm text-text-muted">
            {t('reports:controls.asOf', { date: formatDate(new Date(), { month: 'long', day: 'numeric', year: 'numeric' }) })}
          </span>
          <button
            onClick={() => window.print()}
            className="px-3 py-1 border border-border text-sm text-text-muted hover:bg-bg-elevated transition-colors no-print"
          >
            {t('reports:controls.print')}
          </button>
        </div>
      </div>

      <div className="bg-bg-surface text-text border border-border max-w-2xl print:bg-white print:text-gray-900 print:border-gray-200 print-full">
        <div className="px-4 py-3 border-b border-border print:border-gray-200">
          <h3 className="text-base font-bold text-text print:text-gray-900">{t('reports:titles.balanceSheet')}</h3>
          <p className="text-xs text-text-muted mt-0.5 print:text-gray-500">
            {t('reports:controls.asOf', { date: formatDate(new Date(), { month: 'long', day: 'numeric', year: 'numeric' }) })}
            {hasForeignAccounts && ` · ${t('reports:footnotes.allAmountsIn', { homeCurrency })}`}
          </p>
        </div>

        <SectionTable label={t('reports:sections.assets')} rows={assets} total={totalAssets} />
        <SectionTable label={t('reports:sections.liabilities')} rows={liabilities} total={totalLiabilities} />
        {equity.length > 0 && <SectionTable label={t('reports:sections.equity')} rows={equity} total={totalEquity} />}

        <table className="w-full border-t-2 border-border-strong print:border-gray-300">
          <tbody>
            <tr className="border-b border-border print:border-gray-200">
              <td className="py-1.5 px-4 text-sm font-semibold text-text-muted print:text-gray-700">{t('reports:sections.totalLiabilitiesEquity')}</td>
              <td />
              <td className="py-1.5 px-4 text-right text-sm font-mono font-semibold text-text print:text-gray-900">
                {formatAmount(totalLiabilities + totalEquity, homeCurrency)}
              </td>
            </tr>
            <tr>
              <td className="py-2 px-4 text-base font-bold text-text print:text-gray-900">{t('reports:sections.netWorthFormula')}</td>
              <td />
              <td className={`py-2 px-4 text-right text-base font-mono font-bold ${totalAssets + totalLiabilities >= 0 ? 'text-positive print:text-green-700' : 'text-negative print:text-red-700'}`}>
                {formatAmount(totalAssets + totalLiabilities, homeCurrency)}
              </td>
            </tr>
          </tbody>
        </table>

        {hasForeignAccounts && (
          <p className="px-4 py-2 text-xs text-text-subtle border-t border-border print:text-gray-400 print:border-gray-100">
            * {t('reports:footnotes.balanceSheetConversion', { homeCurrency, date: today })}
          </p>
        )}
      </div>
    </div>
  );
}
